import React from "react";
import { Container, Box, Typography } from "@mui/material";
import { PageTransition } from "../components/common/PageTransition";
import { Testimonials } from "../components/about/Testimonials";
import { FONT_FAMILY_SERIF } from "../theme";
import { usePublicSiteSettings } from "../hooks/useSiteSettings";
import { useTestimonials } from "../hooks/useTestimonials";
import { SEOHead } from "../components/seo/SEOHead";
import {
  generateOrganizationStructuredData,
  generateBreadcrumbStructuredData,
  generateReviewStructuredData,
  generateAggregateRatingStructuredData,
  getFullUrl,
} from "../utils/seo";
import { Breadcrumbs } from "../components/common/Breadcrumbs";
import { Link } from "react-router-dom";
import { Button } from "@mui/material";
import { useFeaturedGalleries } from "../hooks/useGalleries";

export const AboutPage: React.FC = () => {
  const { data: siteSettings } = usePublicSiteSettings();
  const { data: testimonials } = useTestimonials();
  const { data: featuredGalleries } = useFeaturedGalleries();
  
  // Get about image URL from settings
  const aboutImageUrl = siteSettings?.settings?.about_image_url;
  
  // Get about hero image URL from settings
  const aboutHeroImageUrl = siteSettings?.settings?.about_hero_image_url;

  const aboutDescription =
    "Hi, I'm Mariah. A photographer drawn to honest, unscripted moments. Based in New Jersey, originally from Minnesota. Available for commissions and inquiries.";
  
  // Generate structured data
  const structuredData: any[] = [
    generateOrganizationStructuredData(getFullUrl("/"), aboutImageUrl || undefined),
    generateBreadcrumbStructuredData([
      { name: "Home", url: getFullUrl("/") },
      { name: "About", url: getFullUrl("/about") },
    ]),
  ];
  
  // Add Review schemas for testimonials
  if (testimonials && testimonials.length > 0) {
    // Add individual reviews
    testimonials.forEach((testimonial) => {
      if (testimonial.is_published) {
        structuredData.push(
          generateReviewStructuredData(
            testimonial.client_name,
            testimonial.content,
            testimonial.rating || undefined,
            testimonial.created_at,
            testimonial.client_title || undefined
          )
        );
      }
    });
    
    // Add aggregate rating if we have ratings
    const ratings = testimonials
      .filter((t) => t.is_published && t.rating != null)
      .map((t) => ({ rating: t.rating! }));
    
    if (ratings.length > 0) {
      const aggregateRating = generateAggregateRatingStructuredData(
        ratings,
        ratings.length
      );
      if (aggregateRating) {
        structuredData.push(aggregateRating);
      }
    }
  }

  return (
    <PageTransition>
      <SEOHead
        title="About"
        description={aboutDescription}
        image={aboutHeroImageUrl || aboutImageUrl}
        url={getFullUrl("/about")}
        structuredData={structuredData}
      />
      <Box component="main">
        {/* Breadcrumbs */}
        <Breadcrumbs
          items={[
            { label: "Home", path: "/" },
            { label: "About", path: "/about" },
          ]}
        />
        
        {/* Hero Section */}
        <Box
          component="section"
          aria-label="About hero image"
          sx={{
            position: "relative",
            height: { xs: "50vh", md: "65vh" },
            width: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.05)",
          }}
        >
        {aboutHeroImageUrl && (
            <Box
              component="img"
              key={aboutHeroImageUrl}
              src={aboutHeroImageUrl}
              alt="Mariah Blue Photography workspace and creative space"
              loading="eager"
              style={{
              position: "absolute",
              width: "100%",
              height: "100%",
              objectFit: "cover",
              filter: "none",
              WebkitFilter: "none",
            }}
          />
        )}
      </Box>

        {/* About Content */}
        <Container
          component="section"
          aria-label="About Mariah Blue"
          maxWidth="lg"
          sx={{ mt: { xs: 8, md: 12 }, mb: { xs: 8, md: 12 } }}
        >
        {/* Title - Always on top */}
        <Typography
          variant="h3"
          sx={{
            fontFamily: '"Cinzel Decorative", serif',
            fontSize: { xs: "2rem", sm: "2.5rem", md: "3.5rem", lg: "4rem" },
            fontWeight: 400,
            mb: { xs: 3, md: 4 },
            letterSpacing: "-0.02em",
            lineHeight: 1.1,
          }}
        >
          Hi, I'm Mariah.
        </Typography>

        <Box
          sx={{
            display: { xs: "block", md: "grid" },
            gridTemplateColumns: { md: "5fr 7fr" },
            gap: { md: 8 },
            alignItems: { md: "center" },
            "&::after": {
              content: '""',
              display: { xs: "block", md: "none" },
              clear: "both",
            },
          }}
        >
          {/* Portrait Image */}
          {aboutImageUrl && (
            <Box
              sx={{
                width: { xs: "120px", md: "auto" },
                float: { xs: "left", md: "none" },
                marginRight: { xs: 2, md: 0 },
                marginBottom: { xs: 2, md: 0 },
                flexShrink: { xs: 0 },
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  paddingTop: { xs: "133%", md: "125%" }, // 3:4 aspect ratio on mobile, 4:5 on desktop
                  borderRadius: "4px",
                }}
              >
                <Box
                  component="img"
                  key={aboutImageUrl}
                  src={aboutImageUrl}
                  alt="Mariah Blue - Professional photographer"
                  loading="eager"
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    filter: "none",
                    WebkitFilter: "none",
                  }}
                />
              </Box>
            </Box>
          )}

          {/* Biography */}
          <Box sx={{ maxWidth: { md: "600px" } }}>
            <Box sx={{ maxWidth: { md: "600px" } }}>
              <Typography
                variant="body1"
                sx={{
                  fontFamily: FONT_FAMILY_SERIF,
                  fontSize: "1.0625rem",
                  lineHeight: 1.8,
                  color: "text.secondary",
                  mb: 3,
                }}
              >
                I grew up in Minnesota, the land of 10,000 lakes—endless water, quiet moments, and wide skies that taught me how to slow down and really look.
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  fontFamily: FONT_FAMILY_SERIF,
                  fontSize: "1.0625rem",
                  lineHeight: 1.8,
                  color: "text.secondary",
                  mb: 3,
                }}
              >
                New Jersey is where photography found me. Where I learned to notice what matters most—the subtle gestures, quiet glances, and small sparks of life that often go unseen.
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  fontFamily: FONT_FAMILY_SERIF,
                  fontSize: "1.0625rem",
                  lineHeight: 1.8,
                  color: "text.secondary",
                  mb: 3,
                }}
              >
                Living with OCD and anxiety means my mind rarely rests, but nature is where I find stillness. Photographing birds—watching them move with instinct and freedom—brings me back to the present. The earth has a way of softening the noise, and that sense of peace is something I carry into my work.
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  fontFamily: FONT_FAMILY_SERIF,
                  fontSize: "1.0625rem",
                  lineHeight: 1.8,
                  color: "text.secondary",
                  mb: 3,
                }}
              >
                I'm drawn to moments that are honest. Unscripted. Unpolished. Real.
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  fontFamily: FONT_FAMILY_SERIF,
                  fontSize: "1.0625rem",
                  lineHeight: 1.8,
                  color: "text.secondary",
                  mb: 3,
                }}
              >
                If you feel at home here, I'd be honored to photograph your life as it unfolds—gently, intentionally—as your photographer and, if it feels right, your friend.
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  fontFamily: FONT_FAMILY_SERIF,
                  fontSize: "1.0625rem",
                  lineHeight: 1.8,
                  color: "text.secondary",
                }}
              >
                Have a photo shoot idea you've been wanting to try? Tell me! I'm growing my portfolio and love collaborating on creative sessions—sometimes I offer them free when a concept really lights me up.
              </Typography>

              {/* Contextual Link to Galleries */}
              <Box sx={{ mt: 4 }}>
                <Button
                  component={Link}
                  to="/galleries"
                  variant="outlined"
                  sx={{
                    fontFamily: FONT_FAMILY_SERIF,
                    textTransform: "none",
                    fontSize: "1rem",
                    px: 3,
                    py: 1.5,
                    borderColor: "text.primary",
                    color: "text.primary",
                    "&:hover": {
                      borderColor: "primary.main",
                      backgroundColor: "rgba(0, 0, 0, 0.04)",
                    },
                  }}
                >
                  View My Work
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      </Container>

        {/* Testimonials Section - Only show if testimonials exist */}
        {testimonials && testimonials.length > 0 && (
          <Box
            component="section"
            aria-label="Client testimonials"
            sx={{
              backgroundColor: "rgba(0, 0, 0, 0.02)",
              py: { xs: 2, md: 3 },
            }}
          >
            <Container maxWidth="lg">
                <Typography
                  variant="h3"
                  component="h2"
                  sx={{
                    fontFamily: '"Cinzel Decorative", serif',
                    fontSize: { xs: "2rem", sm: "2.5rem", md: "3.5rem", lg: "4rem" },
                    fontWeight: 400,
                    textAlign: "center",
                    mb: { xs: 3, md: 4 },
                    letterSpacing: "-0.02em",
                    lineHeight: 1.1,
                  }}
                >
                  What Clients Say
                </Typography>
              <Testimonials />
            </Container>
          </Box>
        )}
      </Box>
    </PageTransition>
  );
};
